import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useFields, Utils } from '@pbt/pbt-ui-components'

import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { ImagingOrderStatus } from '~/constants/imaging'
import i18n from '~/locales/i18n'
import {
  completeOrderWithinDashboard,
  editOrderWithinDashboard,
  fetchDashboardDetails,
  getImagingDashboardIsLoading,
  getImagingDashboardRecord,
  getIsFetchingDetails,
} from '~/store/duck/imagingDashboard'
import { getImagingIsLoading } from '~/store/duck/imagingOrders'
import { useOpenInvoice } from '~/store/hooks/finance'
import {
  getFeatureToggle,
  getImagingOrderStatuses,
} from '~/store/reducers/constants'
import type { ImagingOrder } from '~/types'
import { addOriginalBusinessId } from '~/utils'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'
import useDialog from '~/utils/useDialog'

export const useImagingDetails = (itemId: string, onClose: () => void) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Selectors
  const isSaving = useSelector(getImagingIsLoading)
  const isFetching = useSelector(getIsFetchingDetails)
  const isLoadingDashboardRecord = useSelector(getImagingDashboardIsLoading)
  const item = useSelector(getImagingDashboardRecord(itemId))
  const ImagingOrderStatuses = useSelector(getImagingOrderStatuses)
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )

  const [openInvoiceDialog] = useDialog(DialogNames.INVOICE)

  // Fetch dashboard details on mount
  useEffect(() => {
    if (itemId) {
      dispatch(fetchDashboardDetails(itemId))
    }
  }, [itemId, dispatch])

  // Extract item properties
  const {
    client: clientId,
    patient: patientId,
    order,
    assignedVet,
    appointment,
    vendorId,
    businessId,
  } = item || {}

  const openInvoice = useOpenInvoice(clientId, openInvoiceDialog)

  const status = Utils.getConstantName(order?.statusId, ImagingOrderStatuses)

  const completedStatusId = useMemo(
    () =>
      Utils.findConstantByName(
        ImagingOrderStatus.COMPLETED,
        ImagingOrderStatuses,
      )?.id,
    [ImagingOrderStatuses],
  )

  const prevStatusIdForTrackingRef = useRef<{
    orderId: string | undefined
    statusId: string | undefined
  }>({ orderId: undefined, statusId: undefined })

  // Initialize fields
  const { fields, validate, reset } = useFields([
    {
      name: 'name',
      label: i18n.t('Common:IMAGING_REQUEST_NAME'),
      initialValue: order?.name || '',
    },
    {
      name: 'modalityId',
      type: 'select',
      initialValue: order?.modalityId || '',
    },
    {
      name: 'assignedVetId',
      type: 'select',
      initialValue: assignedVet || '',
    },
    {
      name: 'notes',
      label: i18n.t('Common:NOTES'),
      initialValue: (order?.notes || '').trim(),
    },
    {
      name: 'statusId',
      type: 'select',
      initialValue: order?.statusId || '',
    },
  ])

  const { name, modalityId, assignedVetId, notes, statusId } = fields

  useEffect(() => {
    if (!completedStatusId || !order?.id) {
      return
    }

    const prev = prevStatusIdForTrackingRef.current
    const current = statusId.value

    // Only track if we're still on the same order AND status changed to completed
    if (
      prev.orderId === order.id &&
      current === completedStatusId &&
      prev.statusId !== undefined &&
      prev.statusId !== completedStatusId
    ) {
      trackEvent(TrackingEvent.IMAGING_ATTACHMENT_STATUS_COMPLETED, {
        imaging_order_id: order.id,
      })
    }

    prevStatusIdForTrackingRef.current = {
      orderId: order.id,
      statusId: current,
    }
  }, [statusId.value, completedStatusId, order?.id])

  // Reset fields when item changes
  useEffect(() => {
    if (item) {
      reset()
    }
  }, [item])

  // Action handlers
  const saveOrder = () => {
    const isValid = validate()

    if (order?.id) {
      trackEvent(TrackingEvent.IMAGING_ATTACHMENT_SAVE_CLICKED, {
        imaging_order_id: order.id,
        validated: isValid,
      })
    }

    if (isValid) {
      const newOrder = {
        ...(order as ImagingOrder),
        name: name.value,
        modalityId: modalityId.value,
        assignedVetId: assignedVetId.value,
        notes: notes.value,
        statusId: statusId.value,
      }

      dispatch(editOrderWithinDashboard(newOrder, itemId))
    }
  }

  const viewSoap = () => {
    navigate(
      addOriginalBusinessId(
        `/soap/${order?.soapId}`,
        isPatientSharingEnabled ? businessId : null,
      ),
    )
  }

  const markAsReviewed = () => {
    if (order?.id) {
      dispatch(completeOrderWithinDashboard(order.id, itemId))
    }
  }

  const viewImages = () => {
    window.open(order?.resultsUrl)
  }

  const backToDashboard = () => onClose()

  // Computed values
  const canViewImages =
    (status === ImagingOrderStatus.IMAGE_RECEIVED ||
      status === ImagingOrderStatus.COMPLETED) &&
    Boolean(order?.resultsUrl)

  return {
    // State
    isSaving,
    isFetching,
    isLoadingDashboardRecord,
    item,
    status,
    canViewImages,

    // Extracted properties
    clientId,
    patientId,
    order,
    assignedVet,
    appointment,
    vendorId,
    businessId,

    // Fields
    fields,
    name,
    modalityId,
    assignedVetId,
    notes,
    statusId,

    // Actions
    saveOrder,
    viewSoap,
    markAsReviewed,
    viewImages,
    backToDashboard,
    openInvoice,
  }
}
